// utils/formatData.ts

// TEXT FORMATTING

/**
 * Converts snake_case or shorthand action keywords into human-friendly phrases.
 * @param input - The string to format (e.g., "login_attempt", "tfa_required", "success").
 * @returns Formatted string (e.g., "Login Attempt", "TFA Required", "Success").
 */
export function prettyWords(input?: string | null): string {
    if (!input || typeof input !== 'string') return '';
    const specialCases: Record<string, string> = {
        mfa: 'MFA', vps: 'VPS', otp: 'OTP', ip: 'IP', api: 'API', ddos: 'DDoS', jwt: 'JWT'
    };
    return input
        .split('_')
        .map(word => specialCases[word.toLowerCase()] || (word.charAt(0).toUpperCase() + word.slice(1)))
        .join(' ');
}


export const isHttpOk = (code?: number) => typeof code === 'number' && code >= 200 && code < 300;



// One canonical union for every status you use.
// Keep them lower-case to match your normalisation in formatColour().
export type Status =
    // Server
    | 'pending payment' | 'running' | 'starting' | 'online' | 'paused' | 'suspended' | 'offline'
    | 'stopped' | 'stopping' | 'contact support' | 'provisioning' | 'maintenance'
    | 'rebooting' | 'migrating' | 'backing up' | 'restoring' | 'updating' | 'error'
    // Actions
    | 'login_attempt' | 'tfa_required' | 'pending_mfa' | 'success' | 'denied'
    | 'failure' | 'failed' | 'pending' | 'logged'
    // Invoices
    | 'paid' | 'unpaid' | 'refunded' | 'cancelled'
    // Tickets
    | 'high' | 'medium' | 'low' | 'open' | 'closed' | 'answered'
    // Roles
    | 'admin' | 'client' | 'customer' | 'staff';

// Colour map with compile-time completeness check.
export const STATUS_COLOUR_MAP = {
    // Server
    'pending payment': 'amber',
    running: 'green',
    online: 'green',
    starting: 'light-green',
    paused: 'red-darken-4',
    suspended: 'red-darken-4',
    offline: 'red-darken-4',
    stopped: 'red-darken-4',
    stopping: 'red-darken-4',
    'contact support': 'red-darken-4',
    provisioning: 'blue',
    maintenance: 'orange',
    rebooting: 'indigo',
    migrating: 'purple',
    'backing up': 'teal',
    restoring: 'purple',
    updating: 'cyan',
    error: 'error',

    // Actions
    login_attempt: 'blue',
    tfa_required: 'orange',
    pending_mfa: 'orange',
    success: 'green',
    denied: 'red-darken-4',
    failure: 'red-darken-4',
    failed: 'red-darken-4',
    pending: 'amber',
    logged: 'amber',

    // Invoices
    paid: 'green',
    unpaid: 'amber',
    refunded: 'cyan',
    cancelled: 'red-darken-4',

    // Tickets
    high: 'red-darken-4',
    medium: 'amber',
    low: 'green',
    open: 'green',
    closed: 'grey-darken-2',
    answered: 'blue-lighten-1',

    // Roles
    admin: 'primary',
    client: 'green',
    customer: 'green',
    staff: 'blue',
} as const satisfies Record<Status, string>;

/** Return a Vuetify colour name for any status; defaults to 'grey'. */
export function formatColour(status?: string | null): string {
    if (!status) return 'grey';
    const key = status.toLowerCase().trim() as Status;
    return STATUS_COLOUR_MAP[key] ?? 'grey';
}



// PERMISSIONS FORMATTING

export type ServerStatus =
    | 'pending payment'
    | 'running'
    | 'starting'
    | 'online'
    | 'paused'
    | 'suspended'
    | 'offline'
    | 'stopped'
    | 'contact support'
    | 'provisioning'
    | 'maintenance'
    | 'rebooting'
    | 'migrating'
    | 'backing up'
    | 'restoring'
    | 'stopping'
    | 'updating'
    | 'error';

export type MenuAction =
    | 'start'
    | 'shutdown'
    | 'stop'
    | 'reboot'
    | 'console'
    | 'cancel';

export const ACTIONS_BY_STATUS: Record<ServerStatus, readonly MenuAction[]> = {
    'running': ['shutdown', 'stop', 'reboot', 'console', 'cancel'],
    'online': ['shutdown', 'stop', 'reboot', 'console', 'cancel'],
    'stopped': ['start', 'console', 'cancel'],
    'offline': ['start', 'console', 'cancel'],
    'paused': ['reboot', 'console', 'cancel'],
    'pending payment': ['cancel'],
    'contact support': ['cancel'],
    'maintenance': ['cancel'],
    'rebooting': ['console', 'cancel'],
    'starting': ['console', 'cancel'],
    'provisioning': ['console', 'cancel'],
    'migrating': ['cancel'],
    'backing up': ['cancel'],
    'restoring': ['cancel'],
    'stopping': ['cancel'],
    'updating': ['cancel'],
    'suspended': ['cancel'],
    'error': ['cancel'],
} as const;

/** Normalize and look up allowed actions. Unknown/empty -> ['cancel'] */
export function getAllowedActions(status?: string | null): readonly MenuAction[] {
    if (!status) return ['cancel'];
    const key = status.toLowerCase().trim() as ServerStatus;
    return (ACTIONS_BY_STATUS as Record<string, readonly MenuAction[]>)[key] ?? ['cancel'];
}

/** Convenience guard for templates */
export function isActionAllowed(
    status: string | null | undefined,
    action: MenuAction,
): boolean {
    return getAllowedActions(status).includes(action);
}